/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Col, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import moment from 'moment';
import React, {useContext, useEffect, useState} from 'react';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors} from '../../constants/Common';
import FilteredUnit from '../../constants/FilteredUnit';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {sortYearOptions} from '../../pages/orgManager/Utils/InfraStructureProfile/constant/sorYear';
import NumberInput from '../../shared/NumberInput';
import yearFilterArray from '../../shared/YearFilterArray';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import useUserStore from '../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import RegEx from '../../utils/FormUtils/RegEx';
import MomentDatePicker from '../DatePicker/MomentDatePicker';

import {facilityKeyLabel, yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {RefriKey, RefriLabel, RefriTooltip} from './Constants/IP/RefriKeyLabel';
import {unitType, unitTypes} from './interface/unitChange';
import {getUnits} from './Utils/changeUnit';
import {filterSelectOptions} from './Utils/commonFunctions';

const RefriForm: React.FC<{
  currentStep?: number;
  defaultDisplayStep?: boolean;
}> = ({currentStep, defaultDisplayStep = false}) => {
  const isCompanyUserOrAdmin =
    hasUserRole(UserRole.Company_Admin) || hasUserRole(UserRole.Company_user);
  const form = useFormInstance();
  const {getFieldValue, setFields, setFieldValue} = form;
  const [emissionS, setEmissionS] = useState(
    getFieldValue(RefriKey.EMISSION_SOURCE) ?? []
  );
  const {efdbData, facilityId, selectedYear, selectedCountry} = useContext(
    SelectedCountryContext
  );
  const facilityDetails = useCompanyStore.getState().facilityDetails;
  const userDetails = useUserStore.getState().userDetails;
  const {emissionSource, frequency} = useJSONStore.getState();
  const [facilityEfdb, setFacilityEfdb] = useState<any>();
  const refridgentUsed = useJSONStore
    .getState()
    .refridgentUsed.filter((item) => item?.dbName === facilityEfdb?.scope1);
  const [formSelectedCountry, setFormSelectedCountry] = useState<string>('');
  const [facilityOpt, setFacilityOpt] = useState<any[]>([]);
  const [fuelState, setFuelState] = useState<unitType>(unitTypes.WEIGHT);
  const {financialYear} = useCompanyStore.getState().preUnits;
  const [yearEndSplit, setYearEndSplit] = useState<string | undefined>(
    selectedYear?.includes('-')
      ? selectedYear?.split('-')?.[1]
      : selectedYear ?? ''
  );
  const monthEndSplit = financialYear?.split(/(?=[A-Z])/)?.[1];

  const getFinancialYearPickerFallback = () =>
    moment(`${yearEndSplit}-${monthEndSplit}-01`, 'YYYY-MMM-DD').isAfter(
      moment()
    )
      ? moment()
      : moment(`${yearEndSplit}-${monthEndSplit}-01`, 'YYYY-MMM-DD');

  const [uninstallationPickerValue, setUninstallationPickerValue] =
    useState<moment.Moment>();

  const getUninstallationPanelValue = () => {
    const value = getFieldValue(RefriKey.DATE_UNINSTALLATION);
    if (value) {
      return moment(value);
    }
    const installDate = getFieldValue(RefriKey.DATE_INSTALLATION);

    return installDate ? moment(installDate) : getFinancialYearPickerFallback();
  };

  useEffect(() => {
    setFormSelectedCountry(
      facilityOpt?.find((item: any) => {
        return (
          item?._id === (getFieldValue(facilityKeyLabel.key) ?? facilityId)
        );
      })?.country
    );
  }, [facilityOpt]);

  // const monYear: MYConfig[] = [
  //   {
  //     mainTitle: {key: 'quantity', title: 'Quantity'},
  //     unit: getUnits(fuelState, FilteredUnit(formSelectedCountry))
  //   }
  // ];

  useEffect(() => {
    setFacilityOpt(
      facilityDetails?.[
        getFieldValue(yearKeyLabel.key) ?? selectedYear ?? ''
      ] ?? []
    );
    setFacilityEfdb(
      efdbData?.[getFieldValue(yearKeyLabel.key) ?? selectedYear ?? '']?.find(
        (i: any) => {
          return (
            i.facilityId === (getFieldValue(facilityKeyLabel.key) ?? facilityId)
          );
        }
      )
    );
  }, []);

  const [yearOptions, setYearOptions] = useState<any[]>([]);

  useEffect(() => {
    setYearOptions(
      yearFilterArray(
        facilityDetails,
        !isCompanyUserOrAdmin ? userDetails?.facility : undefined
      )
    );
  }, []);

  return (
    <>
      {(currentStep === 0 || defaultDisplayStep) && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              initialValue={selectedYear}
              hidden={defaultDisplayStep}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={sortYearOptions(yearOptions)}
                suffixIcon={<YearCalender />}
                disabled={getFieldValue('_id') !== undefined}
                onChange={(e) => {
                  setYearEndSplit(e?.includes('-') ? e?.split('-')[1] : e);
                  setFacilityOpt(facilityDetails?.[e] ?? []);
                  setFacilityEfdb(() => {
                    return efdbData?.[e]?.find((i: any) => {
                      return (
                        i?.facilityId === getFieldValue(facilityKeyLabel.key)
                      );
                    });
                  });
                  if (getFieldValue(facilityKeyLabel.key)) {
                    setFields([
                      {
                        name: facilityKeyLabel.key,
                        value: undefined,
                        errors: ['Please select facility again']
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {isCompanyUserOrAdmin && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={facilityKeyLabel.key}
                  label={facilityKeyLabel.label}
                  labelCol={{span: 24}}
                  hidden={defaultDisplayStep}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(facilityKeyLabel.label)
                    }
                  ]}
                  initialValue={facilityId}
                >
                  <Select
                    placeholder={`Choose ${facilityKeyLabel.label}`}
                    options={
                      facilityOpt?.map((item: any) => {
                        return {label: item.name, value: item._id};
                      }) ?? []
                    }
                    disabled={getFieldValue('_id') !== undefined}
                    onChange={(e) => {
                      setFacilityEfdb(() => {
                        return efdbData?.[
                          getFieldValue(yearKeyLabel.key)
                        ]?.find((i: any) => {
                          return i?.facilityId === e;
                        });
                      });
                      if (getFieldValue(RefriKey.REFRI_USED)) {
                        setFields([
                          {
                            name: RefriKey.REFRI_USED,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      setFormSelectedCountry(
                        facilityDetails?.[
                          getFieldValue(yearKeyLabel.key) ?? ''
                        ]?.find((item: any) => {
                          return item?._id === e;
                        })?.country
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </>
      )}
      {(currentStep === 1 || defaultDisplayStep) && (
        <>
          {/* <Col className="gutter-row" span={6}> */}
          <Col className="gutter-row">
            <Form.Item
              name={RefriKey.EMISSION_SOURCE}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(RefriLabel.EMISSION_SOURCE)
                }
              ]}
              label={RefriLabel.EMISSION_SOURCE}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Select
                className="select"
                placeholder={`Enter ${RefriLabel.EMISSION_SOURCE}`}
                options={emissionSource}
                onChange={(e) => {
                  setEmissionS(e);
                  if (getFieldValue(RefriKey.REFRI_USED)) {
                    setFields([
                      {
                        name: RefriKey.REFRI_USED,
                        errors: [CommonErrors.selectAgain],
                        value: undefined
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={6}> */}
          <Col className="gutter-row">
            <Form.Item
              name={RefriKey.NO_UNITS}
              label={RefriLabel.NO_UNITS}
              rules={[
                {
                  pattern: RegEx.nonDecimal,
                  message: CommonErrors.nonDecimalNonNegative
                }
              ]}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <NumberInput
                placeholder={`Enter ${RefriLabel.NO_UNITS}`}
                isFormatted={true}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={RefriKey.DATE_INSTALLATION}
              label={RefriLabel.DATE_INSTALLATION}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(RefriLabel.DATE_INSTALLATION)
                }
              ]}
              validateFirst={true}
              hasFeedback
              labelCol={{span: 24}}
            >
              <MomentDatePicker
                disabledDate={(current) => {
                  const endOfFinancialYear = moment(
                    `${moment(`${monthEndSplit} ${yearEndSplit}`, 'MMMM YYYY')
                      .endOf('month')
                      .date()}/${monthEndSplit}/${yearEndSplit}`
                  ).endOf('month');

                  if (current.isAfter(endOfFinancialYear, 'day')) {
                    return true;
                  } else {
                    return current.isAfter(moment(), 'day');
                  }
                }}
                defaultPickerValue={
                  getFieldValue(RefriKey.DATE_INSTALLATION)
                    ? moment(getFieldValue(RefriKey.DATE_INSTALLATION))
                    : getFinancialYearPickerFallback()
                }
                showNow={false}
                inputReadOnly
                format="DD-MM-YYYY"
                style={{width: '100%'}}
                onChange={() => {
                  if (getFieldValue(RefriKey.DATE_UNINSTALLATION)) {
                    setFields([
                      {
                        name: RefriKey.DATE_UNINSTALLATION,
                        value: undefined
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[RefriKey.DATE_INSTALLATION]} noStyle>
              {() => (
                <Form.Item
                  name={RefriKey.DATE_UNINSTALLATION}
                  label={RefriLabel.DATE_UNINSTALLATION}
                  validateFirst={true}
                  labelCol={{span: 24}}
                >
                  <MomentDatePicker
                    disabled={!getFieldValue(RefriKey.DATE_INSTALLATION)}
                    disabledDate={(current) => {
                      const installDate = getFieldValue(
                        RefriKey.DATE_INSTALLATION
                      );
                      if (!installDate) {
                        return true;
                      }

                      return (
                        current.isBefore(moment(installDate), 'day') ||
                        current.isAfter(moment(), 'day')
                      );
                    }}
                    pickerValue={uninstallationPickerValue}
                    onPickerValueChange={setUninstallationPickerValue}
                    onOpenChange={(open) => {
                      if (open) {
                        setUninstallationPickerValue(
                          getUninstallationPanelValue()
                        );
                      }
                    }}
                    showNow={false}
                    inputReadOnly
                    format="DD-MM-YYYY"
                    style={{width: '100%'}}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={6}> */}
          <Col className="gutter-row">
            <Form.Item
              name={RefriKey.REFRI_USED}
              label={RefriLabel.REFRI_USED}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(RefriLabel.REFRI_USED)
                }
              ]}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
              tooltip={{
                icon: <InfoCircleOutlined />,
                title: RefriTooltip.REFRI_USED
              }}
            >
              <Select
                showSearch
                filterOption={filterSelectOptions}
                className="select"
                placeholder={`Choose ${RefriLabel.REFRI_USED}`}
                options={
                  refridgentUsed?.filter(
                    (item) => item?.fuelTypeIds?.includes(emissionS)
                  ).length > 0
                    ? refridgentUsed.filter(
                        (item) => item?.fuelTypeIds?.includes(emissionS)
                      )
                    : refridgentUsed.filter((item) => {
                        return item && item?.fuelTypeIds?.length === 0;
                      })
                }
                onChange={(_, option) => {
                  setFuelState(option?.state);
                }}
              />
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={6}> */}
          <Col className="gutter-row">
            <Form.Item
              name={RefriKey.FREQUENCY_INSPE}
              label={RefriLabel.FREQUENCY_INSPE}
              labelCol={{span: 24}}
              initialValue={null}
              validateFirst
              hasFeedback
              tooltip={{
                icon: <InfoCircleOutlined />,
                title: RefriTooltip.FREQUENCY_INSPE
              }}
            >
              <Select
                allowClear
                className="select"
                placeholder={`Choose ${RefriLabel.FREQUENCY_INSPE}`}
                options={frequency}
                onClear={() => {
                  setFieldValue(RefriKey.FREQUENCY_INSPE, null);
                }}
              />
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={6}> */}
          <Col className="gutter-row">
            <Form.Item
              name={RefriKey.FREQUENCY_NO}
              label={RefriLabel.FREQUENCY_NO}
              rules={[
                {
                  pattern: RegEx.nonDecimal,
                  message: CommonErrors.nonDecimalNonNegative
                }
              ]}
              initialValue={null}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <NumberInput
                className="select"
                placeholder={`Enter ${RefriLabel.FREQUENCY_NO}`}
              />
            </Form.Item>
          </Col>
        </>
      )}
      {(currentStep === 2 || defaultDisplayStep) && (
        <>
          {/* <MonthlyYearlyDrawerForm
            config={monYear}
            data={getFieldValue(TF) as MYData[]}
          /> */}

          <Col className="gutter-row">
            <Form.Item
              name={RefriKey.REFRI_CHARGE}
              label={RefriLabel.REFRI_CHARGE}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(RefriLabel.REFRI_CHARGE)
                },
                {
                  pattern: RegEx.threeDigitDecimalMoreThanZero,
                  message: CommonErrors.threeDigitDecimalMoreThanZero
                }
              ]}
              initialValue={null}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
              tooltip={{
                icon: <InfoCircleOutlined />,
                title: RefriTooltip.REFRI_CHARGE
              }}
            >
              <NumberInput
                className="select"
                placeholder={`Enter ${RefriLabel.REFRI_CHARGE}`}
                addonAfter={getUnits(
                  fuelState,
                  FilteredUnit(formSelectedCountry ?? selectedCountry)
                )}
              />
            </Form.Item>
          </Col>
        </>
      )}
    </>
  );
};

export default RefriForm;
